// UpdateSamplingGroupsModal.js
// Lets a team lead manage sampling group membership on an active event:
// add employees to a group, move them between groups, and remove them.

import React from "react";
import Select from "react-select";
import styles from "./UpdateSamplingGroupsModal.module.css";

export const UpdateSamplingGroupsModal = ({
  isOpen,
  onClose,
  samplingEventData,
  allEmployees,
  groupsActions,
  emitPatch,
}) => {
  const samplingGroups = samplingEventData?.samplingGroups || [];

  // Directory employees not already assigned to any group in this event.
  // Computed inline (not memoized) so it reflects in-place model mutations
  // after each add/move/remove triggers a re-render.
  const assignedIds = new Set();
  samplingGroups.forEach((g) => {
    g.employees.forEach((e) => {
      if (e.employeeId) assignedIds.add(e.employeeId);
    });
  });
  const unassignedEmployees = (allEmployees || []).filter(
    (emp) => emp.employee_id && !assignedIds.has(emp.employee_id)
  );

  if (!isOpen) return null;

  const handleAddEmployee = (group, selectedOption) => {
    if (!selectedOption || !groupsActions?.addGroupEmployees) return;
    groupsActions.addGroupEmployees({
      samplingGroupId: group.id,
      employees: [selectedOption],
      emitPatch,
    });
  };

  const handleMoveEmployee = (rowId, toGroupId) => {
    if (!toGroupId || !groupsActions?.moveGroupEmployee) return;
    groupsActions.moveGroupEmployee({ rowId, toGroupId, emitPatch });
  };

  const handleRemoveEmployee = (rowId) => {
    if (!groupsActions?.removeGroupEmployee) return;
    groupsActions.removeGroupEmployee({ rowId, emitPatch });
  };

  return (
    <div className={styles.overlay}>
      <div className={styles.modal}>
        <div className={styles.header}>
          <h3 className={styles.title}>Update Sampling Groups</h3>
          <button className={styles.closeButton} onClick={onClose}>
            ×
          </button>
        </div>

        <div className={styles.body}>
          {samplingGroups.length === 0 ? (
            <div className={styles.emptyText}>
              This event has no sampling groups.
            </div>
          ) : (
            samplingGroups.map((group) => (
              <div key={group.id} className={styles.groupCard}>
                <h4 className={styles.groupName}>
                  {group.groupName || "Unnamed Group"}
                </h4>

                {group.employees.length > 0 ? (
                  group.employees.map((employee) => {
                    const otherGroups = samplingGroups.filter(
                      (g) => g.id !== group.id
                    );
                    return (
                      <div key={employee.id} className={styles.employeeRow}>
                        <span className={styles.employeeName}>
                          {employee.employeeName || "(unknown employee)"}
                        </span>
                        <div className={styles.rowControls}>
                          {otherGroups.length > 0 && (
                            <select
                              className={styles.moveSelect}
                              value=""
                              onChange={(e) =>
                                handleMoveEmployee(employee.id, e.target.value)
                              }
                            >
                              <option value="">Move to...</option>
                              {otherGroups.map((g) => (
                                <option key={g.id} value={g.id}>
                                  {g.groupName || "Unnamed Group"}
                                </option>
                              ))}
                            </select>
                          )}
                          <button
                            className={styles.removeButton}
                            onClick={() => handleRemoveEmployee(employee.id)}
                          >
                            Remove
                          </button>
                        </div>
                      </div>
                    );
                  })
                ) : (
                  <div className={styles.emptyText}>
                    No employees in this group.
                  </div>
                )}

                <div className={styles.addEmployeeWrap}>
                  <Select
                    options={unassignedEmployees}
                    getOptionLabel={(option) => option.label}
                    getOptionValue={(option) => option.employee_id}
                    onChange={(selected) => handleAddEmployee(group, selected)}
                    value={null}
                    placeholder="Add employee to this group..."
                    isDisabled={unassignedEmployees.length === 0}
                    menuPortalTarget={document.body}
                    styles={{
                      menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                    }}
                  />
                </div>
              </div>
            ))
          )}
        </div>

        <div className={styles.footer}>
          <button className={styles.doneButton} onClick={onClose}>
            Done
          </button>
        </div>
      </div>
    </div>
  );
};
